import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wrench, Plus, X, Search, Camera, Upload, Trash2, Calendar, Hash, Cog, History } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ASSET_TYPES = [
  'Water Heater', 'Tankless Water Heater', 'Sump Pump', 'Sewer Ejector',
  'Water Softener', 'Backflow Device', 'Recirculation System', 'Main Water Valve',
  'RPZ', 'Lift Station',
];

const empty = {
  property_id: '', asset_type: 'Water Heater', unit_number: '', building: '', floor: '',
  location_notes: '', manufacturer: '', model: '', serial_number: '', asset_tag: '',
  install_date: '', warranty_expiration: '', last_service_date: '', notes: '',
};

const fmt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return s; } };

const PmAssets = () => {
  const [props, setProps] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ property_id: '', asset_type: '', q: '' });
  const [drawer, setDrawer] = useState(null); // 'new' | asset_id
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const loadProps = () => axios.get(`${API}/admin/pm/properties`, { withCredentials: true }).then((r) => setProps(r.data));
  const loadAssets = () => {
    const params = {};
    if (filters.property_id) params.property_id = filters.property_id;
    if (filters.asset_type) params.asset_type = filters.asset_type;
    if (filters.q) params.q = filters.q;
    return axios.get(`${API}/admin/pm/assets`, { withCredentials: true, params }).then((r) => setRows(r.data));
  };

  useEffect(() => { loadProps(); loadAssets(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadAssets(); /* eslint-disable-next-line */ }, [filters.property_id, filters.asset_type]);

  const openCreate = () => { setForm({ ...empty, property_id: filters.property_id || (props[0]?.id || '') }); setSelected(null); setDrawer('new'); };
  const openEdit = (a) => { setForm({ ...empty, ...a }); setSelected(a); setDrawer(a.id); };

  const save = async () => {
    try {
      if (selected) {
        const { data } = await axios.patch(`${API}/admin/pm/assets/${selected.id}`, form, { withCredentials: true });
        toast.success('Asset updated');
        setSelected(data);
      } else {
        const { data } = await axios.post(`${API}/admin/pm/assets`, form, { withCredentials: true });
        toast.success('Asset created');
        setSelected(data);
        setDrawer(data.id);
      }
      loadAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this asset? It will be hidden but service history is preserved.')) return;
    try {
      await axios.delete(`${API}/admin/pm/assets/${selected.id}`, { withCredentials: true });
      toast.success('Archived');
      setDrawer(null);
      loadAssets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const uploadPhoto = async (file) => {
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/pm/assets/${selected.id}/photos`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      const { data: fresh } = await axios.get(`${API}/admin/pm/assets/${selected.id}`, { withCredentials: true });
      setSelected(fresh);
    } catch (e) {
      toast.error('Upload failed');
    }
    setPhotoUploading(false);
  };

  const deletePhoto = async (fileId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/admin/pm/assets/${selected.id}/photos/${fileId}`, { withCredentials: true });
      const { data: fresh } = await axios.get(`${API}/admin/pm/assets/${selected.id}`, { withCredentials: true });
      setSelected(fresh);
    } catch (e) { /* noop */ }
  };

  const photoUrl = (id) => `${API}/admin/pm/assets/photo/${id}`;

  return (
    <div data-testid="admin-pm-assets" className="p-6">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MANAGEMENT</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Asset Tracking</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{rows.length} active asset{rows.length === 1 ? '' : 's'} across {props.length} propert{props.length === 1 ? 'y' : 'ies'}</p>
        </div>
        <button data-testid="asset-new-btn" onClick={openCreate} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] h-10 px-4 rounded font-medium hover:bg-[#F3E5AB]">
          <Plus className="w-4 h-4" /> Add Asset
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select data-testid="asset-filter-property" value={filters.property_id} onChange={(e) => setFilters({ ...filters, property_id: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All properties</option>
          {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="asset-filter-type" value={filters.asset_type} onChange={(e) => setFilters({ ...filters, asset_type: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All types</option>
          {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#525252]" />
          <input data-testid="asset-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && loadAssets()} placeholder="Serial, tag, model, manufacturer…" className="bg-transparent flex-1 text-white text-sm outline-none" />
          <button onClick={loadAssets} className="text-xs text-[#A3A3A3] hover:text-white">Search</button>
        </div>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-[#525252] text-[10px] uppercase">
            <tr>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Unit / Loc</th>
              <th className="p-3 text-left">Manufacturer · Model</th>
              <th className="p-3 text-left">Serial</th>
              <th className="p-3 text-left">Installed</th>
              <th className="p-3 text-left">Warranty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} data-testid={`asset-row-${a.id}`} onClick={() => openEdit(a)} className="border-t border-[#262626] hover:bg-[#1a1a1a] cursor-pointer">
                <td className="p-3 text-[#D4AF37]"><Cog className="w-3 h-3 inline mr-1.5" />{a.asset_type}</td>
                <td className="p-3 text-white">{a.property_name}</td>
                <td className="p-3 text-[#A3A3A3] text-xs">{a.unit_number ? `#${a.unit_number}` : 'Property-level'}{a.location_notes ? ` · ${a.location_notes}` : ''}</td>
                <td className="p-3 text-white text-xs">{a.manufacturer || '—'} {a.model && <span className="text-[#A3A3A3]">· {a.model}</span>}</td>
                <td className="p-3 text-emerald-300 font-mono text-xs">{a.serial_number || '—'}</td>
                <td className="p-3 text-[#A3A3A3] text-xs">{fmt(a.install_date)}</td>
                <td className="p-3 text-[#A3A3A3] text-xs">{fmt(a.warranty_expiration)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#525252]">No assets yet. Click <span className="text-[#D4AF37]">Add Asset</span> to start cataloguing.</td></tr>}
          </tbody>
        </table>
      </div>

      {drawer && (
        <>
          <div className="fixed inset-0 bg-black/70 z-50" onClick={() => setDrawer(null)} />
          <div data-testid="asset-drawer" className="fixed right-0 top-0 h-full w-full max-w-2xl bg-[#0D0D0D] border-l border-[#262626] z-50 overflow-y-auto">
            <div className="sticky top-0 bg-[#0D0D0D] border-b border-[#262626] p-4 flex items-center justify-between">
              <div>
                <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase">{selected ? 'Edit Asset' : 'New Asset'}</p>
                <h2 className="text-white text-lg font-['Outfit']">{form.asset_type}</h2>
              </div>
              <button onClick={() => setDrawer(null)} className="text-[#A3A3A3] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Asset Type *">
                  <select data-testid="asset-form-type" value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                    {ASSET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Property *">
                  <select data-testid="asset-form-property" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm">
                    <option value="">Select…</option>
                    {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                <Field label="Unit (optional)"><Input testId="asset-form-unit" value={form.unit_number} onChange={(v) => setForm({ ...form, unit_number: v })} placeholder="e.g. 302 — leave blank for property-wide" /></Field>
                <Field label="Building / Floor"><Input value={form.building} onChange={(v) => setForm({ ...form, building: v })} placeholder="Bldg A" /></Field>
                <Field label="Manufacturer"><Input testId="asset-form-mfr" value={form.manufacturer} onChange={(v) => setForm({ ...form, manufacturer: v })} placeholder="Bradford White" /></Field>
                <Field label="Model"><Input value={form.model} onChange={(v) => setForm({ ...form, model: v })} placeholder="RG2D50T6N" /></Field>
                <Field label="Serial Number"><Input testId="asset-form-serial" value={form.serial_number} onChange={(v) => setForm({ ...form, serial_number: v })} mono placeholder="Used for WO auto-link" /></Field>
                <Field label="Asset Tag"><Input testId="asset-form-tag" value={form.asset_tag} onChange={(v) => setForm({ ...form, asset_tag: v })} mono placeholder="QR-ready" /></Field>
                <Field label="Install Date"><Input type="date" value={form.install_date || ''} onChange={(v) => setForm({ ...form, install_date: v })} /></Field>
                <Field label="Warranty Expiration"><Input type="date" value={form.warranty_expiration || ''} onChange={(v) => setForm({ ...form, warranty_expiration: v })} /></Field>
                <Field label="Last Service Date"><Input type="date" value={form.last_service_date || ''} onChange={(v) => setForm({ ...form, last_service_date: v })} /></Field>
                <Field label="Location Notes"><Input value={form.location_notes} onChange={(v) => setForm({ ...form, location_notes: v })} placeholder="Mechanical room, behind boiler" /></Field>
              </div>
              <Field label="Notes">
                <textarea data-testid="asset-form-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-2 text-white text-sm" />
              </Field>

              <div className="flex gap-2 pt-2">
                <button data-testid="asset-save-btn" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] h-10 px-5 rounded font-medium hover:bg-[#F3E5AB]">{selected ? 'Save changes' : 'Create asset'}</button>
                {selected && <button onClick={archive} className="text-red-400 hover:text-red-300 h-10 px-3 rounded text-sm inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Archive</button>}
              </div>

              {/* Photos */}
              {selected && (
                <div className="pt-3 border-t border-[#262626]">
                  <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase mb-2 flex items-center gap-1"><Camera className="w-3 h-3" /> Photos ({selected.photos?.length || 0})</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {(selected.photos || []).map((fid) => (
                      <div key={fid} className="relative group">
                        <img src={photoUrl(fid)} alt="" className="w-full h-24 object-cover rounded border border-[#262626]" />
                        <button onClick={() => deletePhoto(fid)} className="absolute top-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <label className="h-24 border border-dashed border-[#262626] rounded flex items-center justify-center text-[#525252] hover:text-[#D4AF37] hover:border-[#D4AF37] cursor-pointer text-xs">
                      <Upload className="w-4 h-4 mr-1" /> {photoUploading ? 'Uploading…' : 'Add'}
                      <input data-testid="asset-photo-upload" type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                    </label>
                  </div>
                </div>
              )}

              {/* Service History */}
              {selected && (selected.service_history || []).length > 0 && (
                <div className="pt-3 border-t border-[#262626]">
                  <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Service History ({selected.service_history.length})</p>
                  <ul className="divide-y divide-[#1f1f1f] -my-2">
                    {selected.service_history.map((h, i) => (
                      <li key={i} className="py-2.5">
                        <p className="text-sm text-white"><span className="text-[#D4AF37] font-mono">{h.work_order_number}</span> · {h.priority || '—'} · <span className="text-[#A3A3A3]">{h.status}</span></p>
                        <p className="text-xs text-[#525252]">{h.issue}</p>
                        <p className="text-[10px] text-[#525252] mt-0.5 uppercase tracking-wider">Linked {fmt(h.linked_at)} by {h.linked_by_name}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (<div><label className="text-[#A3A3A3] text-xs block mb-1">{label}</label>{children}</div>);
const Input = ({ value, onChange, placeholder, mono, type = 'text', testId }) => (
  <input data-testid={testId} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm ${mono ? 'font-mono' : ''}`} />
);

export default PmAssets;
